import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PRODUCTION_FORMAT_LABELS, type ProductionFormat, type ProjectOverview } from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { ProjectStatePanel } from '../../components/ProjectStatePanel.js';
import { Callout } from '../../components/Callout.js';
import { Icon } from '../../components/Icon.js';
import { NextBestAction } from '../../components/overview/NextBestAction.js';
import { WorkflowMap } from '../../components/overview/WorkflowMap.js';
import { ActionCenter } from '../../components/overview/ActionCenter.js';
import { Readiness } from '../../components/overview/Readiness.js';
import { Dependencies } from '../../components/overview/Dependencies.js';
import { RecentActivity } from '../../components/overview/RecentActivity.js';
import { When } from '../../components/overview/parts.js';
import { FORMAT_ICON } from '../NewProjectPage.js';

/** While something is running, the answer is re-read this often; otherwise never on a timer. */
const WHILE_RUNNING_MS = 5_000;

/** Work in flight: a screenplay being read, or the AI analysis. */
export function isRunning(overview: ProjectOverview | undefined): boolean {
  if (overview === undefined) return false;

  return overview.nextAction.kind === 'WAIT' || Object.values(overview.nodes).some((n) => n.state === 'PROCESSING');
}

/**
 * THE PRODUCTION COMMAND CENTER (owner brief, 2026-10-29; redesigned 2026-10-30).
 *
 * One column for where the production is and the one thing to do next (the next
 * action, the workflow strip, the progress detail folded under it), and a rail
 * for everything else that needs a person (the other actions, the issues nothing
 * else names, the latest activity). Each fact is said once: the readiness cards,
 * the scenes gate and the repeated next action are gone from the page.
 *
 * Where the production is, what needs a person, and what to do next — from
 * `GET /v1/projects/:id/overview`, which counts the rows and works out the answer
 * on the server (`deriveOverview`, shared). This page renders it; it does not
 * count, rank or decide anything itself.
 *
 * FRESH BY DEFAULT, POLLED ONLY WHILE WORK RUNS. The query is stale at once, so
 * coming back from confirming scenes or reviewing requirements re-reads it on
 * mount; while a screenplay is being read or the analysis runs it is re-read
 * every five seconds, and the timer stops when nothing is running.
 *
 * FAILURE STAYS WHERE IT HAPPENS. Recent activity is a separate request; a
 * section the API could not read comes back marked as such, and each surface
 * says so instead of showing a zero.
 */
export function OverviewTab({
  projectId,
  address,
  format,
  derivedHealth,
  healthOverride,
}: {
  projectId: string;
  /** The production's own address, for every link this page makes. */
  address: string;
  format: ProductionFormat;
  derivedHealth: string;
  healthOverride: string | null;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const overview = useQuery({
    queryKey: ['project-overview', projectId],
    queryFn: () => api.projectOverview(projectId),
    refetchInterval: (query) => (isRunning(query.state.data) ? WHILE_RUNNING_MS : false),
  });

  const activity = useQuery({
    queryKey: ['overview-activity', projectId],
    queryFn: () => api.overviewActivity(projectId),
  });

  // Schedule red flags, as before: a plan without scheduling answers 403, which
  // is "no schedule here", not a failure to report.
  const warnings = useQuery({
    queryKey: ['schedule-warnings', projectId],
    queryFn: () => api.scheduleWarnings(projectId),
    retry: false,
  });
  const scheduleUnavailable =
    warnings.isError && !(warnings.error instanceof ApiError && (warnings.error.status === 403 || warnings.error.status === 404));

  const data = overview.data;
  const screenplay = data?.facts.screenplay.status === 'OK' ? data.facts.screenplay.value : null;
  const lastActivity = activity.data?.items[0]?.at ?? null;
  const setupOpen = data !== undefined && data.facts.setup.setupCompletedAt === null && data.facts.setup.nextStep !== null;

  return (
    <div data-testid="overview-tab" className="cc">
      <header className="cc-meta" data-testid="overview-header">
        <span className="cc-meta-item" data-testid="overview-format">
          <Icon name={FORMAT_ICON[format]} size={15} />
          <strong>{PRODUCTION_FORMAT_LABELS[format]}</strong>
        </span>
        {screenplay !== null ? (
          <span className="cc-meta-item" data-testid="overview-draft">
            <Icon name="script" size={15} />
            Screenplay <strong>draft v{screenplay.versionNumber}</strong>
          </span>
        ) : null}
        {lastActivity !== null ? (
          <span className="cc-meta-item" data-testid="overview-last-activity">
            <Icon name="clock" size={15} />
            Last activity <strong><When at={lastActivity} /></strong>
          </span>
        ) : null}
        <span className="cc-meta-actions">
          {setupOpen ? (
            <Link to={`/projects/${address}/setup`} className="ctl" data-testid="overview-continue-setup">
              Continue setup
            </Link>
          ) : null}
          <button
            type="button"
            className="ctl"
            data-testid="overview-settings-toggle"
            aria-expanded={settingsOpen}
            aria-controls="overview-settings"
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            Project settings
          </button>
        </span>
      </header>

      {settingsOpen ? (
        <div id="overview-settings" className="cc-panel" data-testid="overview-settings">
          <ProjectStatePanel projectId={projectId} derivedHealth={derivedHealth} healthOverride={healthOverride} />
        </div>
      ) : null}

      {overview.isError ? (
        <Callout
          tone="danger"
          title="Could not load the overview"
          testId="overview-error"
          action={
            <button
              type="button"
              className="ctl"
              data-testid="overview-retry"
              onClick={() => {
                void overview.refetch();
              }}
            >
              Try again
            </button>
          }
        >
          Nothing is lost — this is a problem reaching the server, not with your production. Every tab still works.
        </Callout>
      ) : data === undefined ? (
        <OverviewSkeleton />
      ) : (
        <div className="cc-layout">
          <div className="cc-col-main">
            <NextBestAction
              next={data.nextAction}
              address={address}
              readOnly={data.facts.readOnly}
              onRetry={() => {
                void overview.refetch();
              }}
            />
            <WorkflowMap nodes={data.nodes} address={address} />
            <Readiness facts={data.facts} nodes={data.nodes} />
          </div>

          <aside className="cc-col-rail" aria-label="What else needs you">
            <ActionCenter actions={data.actions} next={data.nextAction} address={address} readOnly={data.facts.readOnly} />
            <Dependencies
              facts={data.facts}
              actions={data.actions}
              next={data.nextAction}
              address={address}
              scheduleWarnings={warnings.data}
              scheduleUnavailable={scheduleUnavailable}
              onRetry={() => {
                void overview.refetch();
              }}
            />
            <RecentActivity projectId={projectId} address={address} query={activity} />
          </aside>
        </div>
      )}
    </div>
  );
}

/** The page's shape in grey while the first answer is on its way — nothing jumps when it lands. */
function OverviewSkeleton() {
  const block = (height: number) => <div className="cc-skel" style={{ height }} />;

  return (
    <div className="cc-layout" data-testid="overview-loading" role="status" aria-label="Loading the overview">
      <div className="cc-col-main">
        {block(150)}
        {block(260)}
      </div>
      <div className="cc-col-rail">
        {block(240)}
        {block(200)}
      </div>
    </div>
  );
}
